const PREFIX = "fieldprint.formDraft.v1:";
const MAX_AGE = 7 * 86400000;
const cache = new Map();
let database;
const restoredNewEditors = new Set();

export function draftUuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  return [...bytes].map((b, i) => `${[4, 6, 8, 10].includes(i) ? "-" : ""}${b.toString(16).padStart(2, "0")}`).join("");
}

export function formDraftKey({ user, impersonatedEmail, growerPublicId }, resource) {
  const identity = [user?.account?.homeAccountId || user?.email || "anonymous",
    impersonatedEmail || "", growerPublicId || "",
    user?.provider === "e2e" ? window.localStorage.getItem("fieldprint.playwright.userEmail") || "" : ""];
  // Keep old keys readable during migration; normal production scopes include the identity lane.
  if (user?.account) identity.push(user.account.tenantId || "", user.account.idTokenClaims?.aud || "");
  return `${PREFIX}${JSON.stringify(identity)}:${resource}`;
}

function openDatabase() {
  if (!window.indexedDB) return Promise.resolve(null);
  if (!database) database = new Promise((resolve) => {
    const request = window.indexedDB.open("fieldprint-form-drafts", 1);
    request.onupgradeneeded = () => request.result.createObjectStore("drafts");
    request.onsuccess = () => { request.result.onversionchange = () => { request.result.close(); database = null; }; resolve(request.result); };
    request.onerror = () => { database = null; resolve(null); };
    request.onblocked = () => { database = null; resolve(null); };
  });
  return database;
}

async function durableStorage(key, value, remove = false) {
  const db = await openDatabase();
  if (db) {
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction("drafts", "readwrite");
        if (remove) tx.objectStore("drafts").delete(key); else tx.objectStore("drafts").put(value, key);
        tx.oncomplete = resolve; tx.onerror = reject; tx.onabort = reject;
      });
      return "device";
    } catch { /* Try durable browser fallback before reporting unavailable storage. */ }
  }
  try {
    if (remove) window.localStorage.removeItem(key); else window.localStorage.setItem(key, JSON.stringify(value));
    return "device";
  } catch { return "tab"; }
}

function legacyKey(key) {
  try {
    if (!key.startsWith(PREFIX)) return null;
    const split = key.indexOf("]:", PREFIX.length);
    const identity = JSON.parse(key.slice(PREFIX.length, split + 1));
    const resource = key.slice(split + 2).replace(/:new:[0-9a-f-]{36}$/, ":new");
    const candidate = `${PREFIX}${JSON.stringify(identity.slice(0, 4))}:${resource}`;
    return candidate !== key ? candidate : null;
  } catch { return null; }
}

function usable(draft) { return draft?.value && Date.now() - draft.savedAt < MAX_AGE ? draft : null; }
function parse(value) { try { return usable(JSON.parse(value)); } catch { return null; } }

export function readFormDraft(key) {
  try { return usable(cache.get(key)) || parse(window.sessionStorage.getItem(key)) || parse(window.localStorage.getItem(key)); }
  catch { return usable(cache.get(key)); }
}

function newEditorParent(key) { return /:new:[0-9a-f-]{36}$/.test(key) ? key.replace(/:[0-9a-f-]{36}$/, "") : null; }

async function storedCandidates(key) {
  const candidates = [];
  const parent = newEditorParent(key);
  const prefix = parent ? `${parent}:` : `${key}:candidate:`;
  const db = await openDatabase();
  if (db) {
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction("drafts", "readwrite"), store = tx.objectStore("drafts");
        const primary = store.get(key);
        primary.onsuccess = () => { const candidate = usable(primary.result); if (candidate) candidates.push({ ...candidate, storageKey: candidate.storageKey || key }); else if (primary.result) store.delete(key); };
        const range = window.IDBKeyRange.bound(prefix, `${prefix}￿`);
        const request = store.openCursor(range);
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          const candidate = usable(cursor.value);
          if (candidate) candidates.push({ ...candidate, storageKey: candidate.storageKey || String(cursor.key).split(":candidate:")[0] });
          else cursor.delete();
          cursor.continue();
        };
        tx.oncomplete = resolve; tx.onerror = reject; tx.onabort = reject;
      });
    } catch { /* Persistent fallback may still contain a copy. */ }
  }
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const storageKey = window.localStorage.key(i);
      if (storageKey === key || storageKey.startsWith(prefix)) {
        const candidate = parse(window.localStorage.getItem(storageKey));
        if (candidate) candidates.push({ ...candidate, storageKey: candidate.storageKey || storageKey.split(":candidate:")[0] });
        else { window.localStorage.removeItem(storageKey); i -= 1; }
      }
    }
  } catch { /* Storage capability is reported on checkpoint. */ }
  return candidates;
}

export async function readFormDraftAsync(key) {
  // A tab's own candidate wins over another tab's newer shared primary copy.
  let own;
  try { own = usable(cache.get(key)) || parse(window.sessionStorage.getItem(key)); } catch { own = usable(cache.get(key)); }
  const candidates = await storedCandidates(key);
  let result = own || candidates.sort((a, b) => b.savedAt - a.savedAt)[0] || null;
  if (!result && legacyKey(key)) {
    result = readFormDraft(legacyKey(key));
    if (result) {
      const capability = await durableStorage(key, result);
      if (capability === "device") {
        try { window.sessionStorage.removeItem(legacyKey(key)); window.localStorage.removeItem(legacyKey(key)); } catch { /* best effort */ }
      }
    }
  }
  if (result) {
    const unique = new Map();
    for (const candidate of [result, ...candidates]) {
      const id = candidate.writerId || "legacy";
      if (!unique.has(id) || unique.get(id).savedAt < candidate.savedAt) {
        const stored = { ...candidate };
        delete stored.alternatives;
        unique.set(id, stored);
      }
    }
    result = { ...result, alternatives: [...unique.values()].sort((a, b) => b.savedAt - a.savedAt) };
    cache.set(key, result);
  }
  return result;
}

export async function retainFormDraftCandidate(key, value, versionId, metadata = {}) {
  const draft = { ...metadata, storageKey: key, writerId: metadata.writerId || draftUuid(), value: JSON.parse(JSON.stringify(value)), versionId, savedAt: metadata.savedAt ?? Date.now() };
  await durableStorage(`${key}:candidate:${draft.writerId}`, draft);
  return draft;
}

export function writeFormDraft(key, value, versionId, metadata = {}) {
  const draft = { ...metadata, storageKey: key, value: JSON.parse(JSON.stringify(value)), versionId, savedAt: Date.now() };
  cache.set(key, draft);
  try { window.sessionStorage.setItem(key, JSON.stringify(draft)); } catch { /* In-memory recovery remains. */ }
  const writes = [durableStorage(key, draft)];
  if (draft.writerId) writes.push(durableStorage(`${key}:candidate:${draft.writerId}`, draft));
  return Promise.all(writes).then(results => results.includes("device") ? "device" : "tab");
}

async function removeMatching(storageKey, writerId, savedAt, value) {
  const matches = candidate => candidate && candidate.writerId === writerId && (value !== undefined
    ? JSON.stringify(candidate.value) === JSON.stringify(value)
    : savedAt == null || candidate.savedAt === savedAt);
  const db = await openDatabase();
  if (db) {
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction("drafts", "readwrite"), store = tx.objectStore("drafts");
        const request = store.get(storageKey);
        request.onsuccess = () => { if (matches(request.result)) store.delete(storageKey); };
        tx.oncomplete = resolve; tx.onerror = reject; tx.onabort = reject;
      });
    } catch { /* Remove persistent fallback when possible. */ }
  }
  try { if (matches(parse(window.localStorage.getItem(storageKey)))) window.localStorage.removeItem(storageKey); } catch { /* best effort */ }
}

export async function clearFormDraft(key, selected = null) {
  let own;
  try { own = usable(cache.get(key)) || parse(window.sessionStorage.getItem(key)); } catch { own = usable(cache.get(key)); }
  const chosen = selected || own;
  if (!chosen) return;
  if ((!selected || own?.writerId === chosen.writerId) && (selected?.value === undefined || JSON.stringify(own?.value) === JSON.stringify(selected.value))) {
    cache.delete(key);
    try { window.sessionStorage.removeItem(key); } catch { /* best effort */ }
  }
  await removeMatching(key, chosen.writerId, selected?.savedAt, selected?.value);
  if (chosen.writerId) await removeMatching(`${key}:candidate:${chosen.writerId}`, chosen.writerId, selected?.savedAt, selected?.value);
}

// Opaque context/resource names converge on one cloud revision across simultaneous tabs.
// Authorization still comes from the server; this identifier grants no access.
export async function resourceScopedDraftId(contextKey, target) {
  const text = JSON.stringify([contextKey, target.kind, target.farm_id ?? null, target.field_id ?? null, target.resource_id]);
  let bytes;
  if (window.crypto?.subtle && typeof TextEncoder !== "undefined") bytes = new Uint8Array(await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text))).slice(0, 16);
  else {
    bytes = new Uint8Array(16);
    for (let group = 0; group < 4; group += 1) {
      let hash = (2166136261 ^ Math.imul(group + 1, 0x9e3779b9)) >>> 0;
      for (let i = 0; i < text.length; i += 1) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619) >>> 0;
      for (let i = 0; i < 4; i += 1) bytes[group * 4 + i] = (hash >>> (i * 8)) & 255;
    }
  }
  bytes[6] = (bytes[6] & 15) | 80;
  bytes[8] = (bytes[8] & 63) | 128;
  return [...bytes].map((b, i) => `${[4, 6, 8, 10].includes(i) ? "-" : ""}${b.toString(16).padStart(2, "0")}`).join("");
}

export function newEditorDraftKey(baseKey) {
  // Reload reuses this tab's new editor. A fresh navigation/tab gets its own UUID,
  // even when the browser copied sessionStorage while duplicating a tab.
  const slot = `fieldprint.newEditor:${baseKey}`;
  const reload = window.performance?.getEntriesByType?.("navigation")?.[0]?.type === "reload" && !restoredNewEditors.has(baseKey);
  restoredNewEditors.add(baseKey);
  let id;
  try { id = reload ? window.sessionStorage.getItem(slot) : null; } catch { /* storage unavailable */ }
  id ||= draftUuid();
  try { window.sessionStorage.setItem(slot, id); } catch { /* in-page UUID still isolates editors */ }
  return `${baseKey}:${id}`;
}
